import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useUnifiedAuth } from "@/backend/use-unified-auth";
import { OrdersApi, FillsApi, FundingApi } from "@/backend/api-client";
import { useAccountStore, type StoreFill, type StoreFundingPayment } from "@/backend/store";
import {
  parseDecimal,
  type Order as BackendOrder,
  type Fill as BackendFill,
  type FundingPayment as BackendFundingPayment,
} from "@/backend/types";
import type { TradeHistory, FundingHistory, OrderHistory, OrderStatus, OrderType, FillType } from "./types";
import type { MarketSymbol } from "@/solana/constants";
import type { PositionSide } from "@/solana/programs/types";

// Helper to convert backend fill to frontend TradeHistory format
const toTradeHistory = (fill: BackendFill): TradeHistory => {
  const size = parseDecimal(fill.size);
  const price = parseDecimal(fill.price);
  const ts = fill.created_at ? new Date(fill.created_at).getTime() : 0;

  return {
    id: fill.id,
    timestamp: isNaN(ts) ? 0 : ts,
    market: fill.market as MarketSymbol,
    side: (fill.side === "buy" ? "long" : "short") as PositionSide,
    size,
    entryPrice: price, // For fills, we only have execution price
    exitPrice: price, // Same as entry for individual fills
    pnl: parseDecimal(fill.realized_pnl),
    fee: parseDecimal(fill.fee),
    fillType: (fill.fill_type || "open") as FillType,
  };
};

// Helper to convert backend funding payment to frontend FundingHistory format
const toFundingHistory = (payment: BackendFundingPayment): FundingHistory => {
  const positionSize = parseDecimal(payment.position_size);

  return {
    id: payment.id,
    timestamp: new Date(payment.created_at).getTime(),
    market: payment.market as MarketSymbol,
    side: (positionSize >= 0 ? "long" : "short") as PositionSide,
    size: Math.abs(positionSize),
    fundingRate: parseDecimal(payment.funding_rate),
    payment: parseDecimal(payment.payment),
  };
};

// Helper to convert store fill to frontend TradeHistory format
const storeFillToTradeHistory = (fill: StoreFill): TradeHistory => {
  return {
    id: fill.id,
    timestamp: fill.timestamp,
    market: fill.market as MarketSymbol,
    side: (fill.side === "buy" ? "long" : "short") as PositionSide,
    size: fill.size,
    entryPrice: fill.price,
    exitPrice: fill.price,
    pnl: fill.realizedPnl,
    fee: fill.fee,
    fillType: (fill.fillType || "open") as FillType,
  };
};

// Helper to convert store funding payment to frontend FundingHistory format
const storeFundingToFundingHistory = (payment: StoreFundingPayment): FundingHistory => {
  return {
    id: payment.id,
    timestamp: payment.timestamp,
    market: payment.market as MarketSymbol,
    side: (payment.positionSize >= 0 ? "long" : "short") as PositionSide,
    size: Math.abs(payment.positionSize),
    fundingRate: payment.fundingRate,
    payment: payment.payment,
  };
};

// Hooks
export const useTradeHistory = () => {
  const { userId } = useUnifiedAuth();

  // Determine effective user ID (works for both wallet and email users)
  const effectiveUserId = userId;

  // Get fills from store (populated by WebSocket updates)
  const storeFills = useAccountStore((state) => state.fills);

  // Initial HTTP load - fetches fills on mount
  const { data: httpFills = [], isLoading } = useQuery({
    queryKey: ["trade-history", effectiveUserId],
    queryFn: async (): Promise<TradeHistory[]> => {
      if (!effectiveUserId) {
        return [];
      }

      try {
        // Fetch all fills for complete trade history
        const fills = await FillsApi.listAllFills();
        return fills.map(toTradeHistory);
      } catch (error) {
        console.error("Failed to fetch trade history:", error);
        return [];
      }
    },
    enabled: !!effectiveUserId,
    staleTime: 30000, // Data considered fresh for 30 seconds
  });

  // Merge HTTP fills with WebSocket store fills
  // WebSocket updates add new fills, HTTP provides initial load
  const trades = useMemo(() => {
    // Create a map to dedupe by ID (store takes precedence for newer data)
    const fillMap = new Map<string, TradeHistory>();
    
    // Add HTTP fills first
    for (const fill of httpFills) {
      fillMap.set(fill.id, fill);
    }
    
    // Add/override with store fills (real-time updates)
    for (const [, storeFill] of storeFills) {
      fillMap.set(storeFill.id, storeFillToTradeHistory(storeFill));
    }
    
    // Convert to array and sort by timestamp (newest first)
    return Array.from(fillMap.values()).sort((a, b) => b.timestamp - a.timestamp);
  }, [httpFills, storeFills]);

  return {
    trades,
    isLoading,
  };
};

export const useFundingHistoryData = () => {
  const { userId } = useUnifiedAuth();

  // Determine effective user ID (works for both wallet and email users)
  const effectiveUserId = userId;

  // Get funding payments from store (populated by WebSocket updates)
  const storeFunding = useAccountStore((state) => state.fundingPayments);

  // Initial HTTP load - fetches funding payments on mount
  const { data: httpFunding = [], isLoading } = useQuery({
    queryKey: ["funding-history", effectiveUserId],
    queryFn: async (): Promise<FundingHistory[]> => {
      if (!effectiveUserId) {
        return [];
      }

      try {
        const payments = await FundingApi.listPayments();
        return payments.map(toFundingHistory);
      } catch (error) {
        console.error("Failed to fetch funding history:", error);
        return [];
      }
    },
    enabled: !!effectiveUserId,
    staleTime: 60000, // Data considered fresh for 60 seconds
  });

  // Merge HTTP funding with WebSocket store funding
  // WebSocket updates add new funding payments, HTTP provides initial load
  const fundingHistory = useMemo(() => {
    // Create a map to dedupe by ID (store takes precedence for newer data)
    const fundingMap = new Map<string, FundingHistory>();
    
    // Add HTTP funding first
    for (const payment of httpFunding) {
      fundingMap.set(payment.id, payment);
    }
    
    // Add/override with store funding (real-time updates)
    for (const [, storePayment] of storeFunding) {
      fundingMap.set(storePayment.id, storeFundingToFundingHistory(storePayment));
    }
    
    // Convert to array and sort by timestamp (newest first)
    return Array.from(fundingMap.values()).sort((a, b) => b.timestamp - a.timestamp);
  }, [httpFunding, storeFunding]);

  return {
    fundingHistory,
    isLoading,
  };
};

// Helper to convert backend order to frontend OrderHistory format
const toOrderHistory = (order: BackendOrder): OrderHistory => {
  // Map backend status to frontend status
  const mapStatus = (status: string): OrderStatus => {
    switch (status) {
      case "filled": return "filled";
      case "cancelled": return "cancelled";
      case "rejected": return "cancelled"; // Map rejected to cancelled for display
      case "partially_filled": return "partial";
      default: return "cancelled";
    }
  };

  // For filled orders, always include the price (it's the execution price)
  // For unfilled limit orders, include the limit price
  const price = parseDecimal(order.price);
  
  return {
    id: order.id,
    timestamp: new Date(order.created_at).getTime(),
    type: order.order_type as OrderType,
    market: order.market as MarketSymbol,
    side: order.side,
    size: parseDecimal(order.size),
    filledSize: parseDecimal(order.filled_size),
    price: price > 0 ? price : undefined,
    status: mapStatus(order.status),
  };
};

export const useOrderHistoryData = () => {
  const { userId } = useUnifiedAuth();
  
  // Determine effective user ID (works for both wallet and email users)
  const effectiveUserId = userId;
  
  const { data: orders = [], isLoading } = useQuery({
    queryKey: ["order-history", effectiveUserId],
    queryFn: async (): Promise<OrderHistory[]> => {
      if (!effectiveUserId) {
        return [];
      }
      
      try {
        const backendOrders = await OrdersApi.listOrders();
        
        // Filter for completed orders only (not open/pending)
        const completedOrders = backendOrders.filter(
          (o) => o.status === "filled" || 
                 o.status === "cancelled" || 
                 o.status === "rejected" ||
                 o.status === "partially_filled"
        );
        
        // Convert to frontend format and sort by timestamp (newest first)
        return completedOrders
          .map(toOrderHistory)
          .sort((a, b) => b.timestamp - a.timestamp);
      } catch (error) {
        console.error("Failed to fetch order history:", error);
        return [];
      }
    },
    enabled: !!effectiveUserId,
    staleTime: 30000, // Data considered fresh for 30 seconds
  });
  
  return {
    orderHistory: orders,
    isLoading,
  };
};

